import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Typography from '@mui/material/Typography';

import { FormikField } from '@/components/forms/formik/FormikField';
import { FormikRedux } from '@/components/forms/formik/FormikRedux';
import { FormikSubmit } from '@/components/forms/formik/FormikSubmit';
import { Button } from '@/shared/button/Button';
import { TextField } from '@/shared/textField/TextField';

import { initPasswordResetAction } from '@/store/profile/profile.actions';
import { selectSendResetLinkStatus } from '@/store/profile/profile.selectors';
import { emailSchema } from '@/validation/emailSchema';

import { useStyles } from './SendResetLinkDialog.styles';

type TSendResetLinkDialogProps = {
  initialEmail: string | null | undefined;
};

export const SendResetLinkDialog = ({ initialEmail }: TSendResetLinkDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const { loaded } = useSelector(selectSendResetLinkStatus);

  const [isOpen, setIsOpen] = useState(false);

  const toggleDialog = () => {
    setIsOpen(!isOpen);
  };

  const handleSubmit = ({ email }: { email: string }) => {
    dispatch(initPasswordResetAction.request(email));
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(initPasswordResetAction.clean());
    }

    if (loaded) {
      setIsOpen(false);
    }
  }, [dispatch, loaded, isOpen]);

  return (
    <>
      <Typography
        variant="caption"
        component="p"
        onClick={toggleDialog}
        color="primary"
        data-testid="forgotPassword"
        sx={{ cursor: 'pointer' }}
      >
        {t('landing.signin.forgotPassword')}
      </Typography>

      <Dialog open={isOpen} onClose={toggleDialog} classes={{ paper: classes.resetDialog }}>
        <DialogTitle classes={{ root: classes.title }} component="div">
          <Typography variant="h6">{t('resetPassword.dialog.title')}</Typography>
          <Typography variant="body2">{t('resetPassword.dialog.instruction')}</Typography>
        </DialogTitle>

        <FormikRedux
          storePath="profile.passwordReset.resetInit"
          initialValues={{ email: initialEmail || '' }}
          validationSchema={emailSchema}
          onSubmit={handleSubmit}
        >
          <DialogContent className={classes.content}>
            <FormikField
              name="email"
              type="email"
              label="landing.welcome.emailLabel"
              placeholder="landing.welcome.emailPlaceholder"
              Component={TextField}
            />
          </DialogContent>

          <DialogActions className={classes.actions}>
            <FormikSubmit data-testid="reset-password">
              {t('resetPassword.dialog.reset')}
            </FormikSubmit>

            <Button type="button" variant="text" onClick={toggleDialog}>
              {t('dialog.cancel')}
            </Button>
          </DialogActions>
        </FormikRedux>
      </Dialog>
    </>
  );
};
